import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Card } from '../components/signup/signup.interface';

import { ApiService } from './api.service';
import {
  UserCardsResponse,
  CardDetails,
  VerifyOtpResponse,
  AuthResponse,
  OtpResponse,
  DeleteTokenResponse,
} from './auth.interface';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public unauthorizedEventEmitter$ = new Subject<boolean>();
  private authModalSubject = new BehaviorSubject<{
    visible: boolean;
    activeScreen: string;
  }>({ visible: false, activeScreen: '' });
  public authModalSubject$ = this.authModalSubject.asObservable();
  public cardDetails: CardDetails = {
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
  };

  public constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private storageService: StorageService
  ) {}

  public showModal(activeScreen: string): void {
    this.authModalSubject.next({ visible: true, activeScreen });
  }

  public hideModal(): void {
    this.authModalSubject.next({ visible: false, activeScreen: '' });
  }

  public initiate3dsPayment(): Observable<AuthResponse> {
    const { cardNumber, expiryMonth, expiryYear }: CardDetails =
      (this.cardDetails as CardDetails) ?? {};
    if (!cardNumber || !expiryMonth || !expiryYear) {
      return this.handleErrorState(
        new HttpErrorResponse({ error: 'Card details not found' })
      );
    }
    const payload = {
      number: cardNumber,
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
    };
    return this.http
      .post<AuthResponse>(
        environment.apiClientsServiceUrl + '/card_secure/login',
        payload
      )
      .pipe(
        map((res: AuthResponse) => res),
        catchError((error) => this.handleErrorState(error))
      );
  }

  private handleErrorState(error: HttpErrorResponse): Observable<never> {
    if (
      error.status === 401 &&
      Array.isArray(error?.error?.errors) &&
      error.error.errors.length &&
      error.error.errors[0] === 'Card not verified'
    ) {
      console.error('Card not verified, please check');
    } else if (error.status === 401) {
      this.clearStorage();
      this.unauthorizedEventEmitter$.next(true);
    }
    return throwError(() => error);
  }

  public saveInLocalStorage(key: string, value: string | object): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  public getFromLocalStorage<T>(key: string): T | null {
    const val = localStorage.getItem(key);
    if (val !== null) {
      return JSON.parse(val);
    }
    return null;
  }

  public clearStorage(): void {
    this.storageService.clearLocalStorage();
    this.storageService.clearSessionStorage();
  }

  public getCards(params = {}): Observable<UserCardsResponse> {
    return this.http
      .get<UserCardsResponse>(
        environment.apiClientsServiceUrl + '/user/user_cards',
        {
          params: params,
        }
      )
      .pipe(
        map((res: UserCardsResponse) => res || []),
        catchError((error) => throwError(error))
      );
  }

  public generateOtp(
    isSignUp: boolean,
    email: string
  ): Observable<OtpResponse> {
    let payload: { card_num?: string; email: string };
    let url: string;
    if (isSignUp) {
      const cardDetails: CardDetails = (this.cardDetails as CardDetails) ?? {};
      payload = {
        card_num: cardDetails?.cardNumber,
        email,
      };
      url = environment.apiClientsServiceUrl + `/auth/init`;
    } else {
      payload = {
        email,
      };
      url = environment.apiClientsServiceUrl + `/auth/login`;
    }
    return this.http.post(url, payload).pipe(
      map((res: OtpResponse) => res),
      catchError(
        (error: {
          error: {
            errors: string[];
            wrong_attempts_left: number;
            user_unlock_at: Date | string;
            next_otp_at: Date | string;
          };
        }) =>
          this.handleErrorState(
            new HttpErrorResponse({ error: error.error, status: 400 })
          )
      )
    );
  }

  public verifyOtp(
    userId: string,
    payload: { code: string; otp_type?: string },
    captchaToken: string
  ): Observable<VerifyOtpResponse> {
    const headers: { [key: string]: string } = {};
    if (environment.recaptchaFlag && environment.production) {
      headers['x-recaptcha-token'] = captchaToken;
    }

    return this.http
      .post(
        environment.apiClientsServiceUrl + `/users/${userId}/otps/verify`,
        payload,
        {
          headers,
        }
      )
      .pipe(
        map((res: VerifyOtpResponse) => res),
        catchError(
          (error: {
            error: {
              errors: string[];
            };
          }) =>
            this.handleErrorState(
              new HttpErrorResponse({ error: error.error, status: 400 })
            )
        )
      );
  }

  public verifyTransaction(transactionId?: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        environment.apiClientsServiceUrl + '/card_secure/verify',
        {
          id: transactionId,
        }
      )
      .pipe(
        map((res: AuthResponse) => res),
        catchError((error) => this.handleErrorState(error))
      );
  }

  public isAuthenticated(): boolean {
    if (this.getFromLocalStorage('isSignUp') === 'true') {
      return false;
    }
    return !!this.getFromLocalStorage('currentUser');
  }

  public deleteToken(): Observable<DeleteTokenResponse> {
    return this.http
      .post<DeleteTokenResponse>(
        environment.apiClientsServiceUrl + '/sessions/' + 'delete',
        {}
      )
      .pipe(
        map((res: DeleteTokenResponse) => {
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      );
  }

  public setCard(card: Card): void {
    const { id } = card.attributes;

    if (id) {
      // TODO (10/1/2025): Add a type for the response by updating it auth journeys or pv-shared-services
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.apiService.validateCard(id).subscribe((res: any) => {
        if (res?.data?.attributes?.id == card.attributes.id) {
          const current_user = JSON.parse(
            localStorage.getItem('currentUser') || '{}'
          );
          if (current_user && current_user?.token) {
            current_user.token = res.data.attributes.session_token;
            localStorage.setItem('currentUser', JSON.stringify(current_user));
            // For Visa Hajj, also populate pvCurrentState with the token
            if (environment.populatePvCurrentState) {
              const currentState = JSON.parse(
                localStorage.getItem('pvCurrentState') ?? 'null'
              );
              localStorage.setItem(
                'pvCurrentState',
                JSON.stringify({
                  ...(currentState || {}),
                  token: res.data.attributes.session_token,
                })
              );
            }
            localStorage.setItem('currentUserCard', res.data.attributes.id);
            window.location.reload();
          }
        }
      });
    }
  }
}
